import type { components } from "../../../api/generated";
import type {
  ApprovalFlowStep,
  ApprovalMemberCandidates,
  ApprovalScopeSupplier,
  ApprovalSettingsDraft,
  ApprovalStepDraft,
  ApprovalRoundDetail,
  ApprovalRoundPage,
  ApprovalRoundStep,
  ApprovalTypeSettings,
} from "../types";

export type ApprovalTypeSettingsRaw = components["schemas"]["ApprovalTypeSettingsDTO"];
export type ApprovalMemberCandidatesRaw = components["schemas"]["ApprovalMemberCandidatesDTO"];
export type ApprovalSupplierRaw = components["schemas"]["ApprovalSupplierDTO"];
type ApprovalFlowStepRaw = components["schemas"]["ApprovalFlowStepDTO"];
type ApprovalSettingsMutationRaw = components["schemas"]["ApprovalSettingsMutation"];
type ApprovalFlowStepMutationRaw = components["schemas"]["ApprovalFlowStepMutation"];
export type ApprovalRoundPageRaw = components["schemas"]["ApprovalRoundPageDTO"];
export type ApprovalRoundDetailRaw = components["schemas"]["ApprovalRoundDetailDTO"];
export type ApprovalActionMutationRaw = components["schemas"]["ApprovalActionMutation"];

/** 在契约边界映射一种审批类型的设置，流程、级与成员保持后端顺序。 */
export function mapApprovalTypeSettings(raw: ApprovalTypeSettingsRaw): ApprovalTypeSettings {
  return {
    typeCode: raw.type_code,
    typeLabel: raw.type_label,
    branchDimensions: raw.branch_dimensions,
    version: raw.version,
    mainFlow: raw.main_flow ? raw.main_flow.steps.map(mapStep) : null,
    branches: raw.branches.map((branch) => ({
      branchNo: branch.branch_no,
      scopeDimension: branch.scope_dimension,
      suppliers: branch.settlement_suppliers.map(mapApprovalSupplier),
      departments: branch.departments.map((dept) => ({ deptId: dept.dept_id, name: dept.name, disabled: dept.disabled })),
      steps: branch.steps.map(mapStep),
    })),
    updatedByName: raw.updated_by_name ?? null,
    updatedAt: raw.updated_at ?? null,
  };
}

/** 映射一个结算主体。 */
export function mapApprovalSupplier(raw: ApprovalSupplierRaw): ApprovalScopeSupplier {
  return { supplierId: raw.supplier_id, name: raw.name, disabled: raw.disabled };
}

/** 映射挑人用的部门与员工。 */
export function mapApprovalMemberCandidates(raw: ApprovalMemberCandidatesRaw): ApprovalMemberCandidates {
  return {
    departments: raw.departments.map((item) => ({ deptId: item.dept_id, parentId: item.parent_id ?? null, name: item.name })),
    members: raw.members.map((item) => ({
      userId: item.user_id, name: item.name, mainDeptId: item.main_dept_id ?? null, disabled: item.disabled,
    })),
  };
}

/** 编辑稿转成整套保存的请求体：不用的分支不发，分支只发当前维度那一组范围，所选部门的审批人不带成员。 */
export function mapApprovalSettingsMutation(draft: ApprovalSettingsDraft): ApprovalSettingsMutationRaw {
  return {
    version: draft.version,
    main_flow: { steps: draft.mainFlow.map(mapStepMutation) },
    branches: draft.branches.flatMap((branch, index) => (branch
      ? [{
          branch_no: index + 1,
          scope_dimension: branch.scopeDimension,
          settlement_supplier_ids: branch.scopeDimension === "settlement_supplier" ? branch.supplierIds : [],
          department_ids: branch.scopeDimension === "department" ? branch.departmentIds : [],
          steps: branch.steps.map(mapStepMutation),
        }]
      : [])),
  };
}

/** 映射一级，成员保持后端顺序。 */
function mapStep(step: ApprovalFlowStepRaw): ApprovalFlowStep {
  return {
    stepNo: step.step_no,
    approverType: step.approver_type,
    members: step.members.map((member) => ({ userId: member.user_id, name: member.name, disabled: member.disabled })),
  };
}

/** 一级转成请求体；所选部门的审批人不带成员。 */
function mapStepMutation(step: ApprovalStepDraft): ApprovalFlowStepMutationRaw {
  return { approver_type: step.approverType, member_user_ids: step.approverType === "members" ? step.memberIds : [] };
}

/** 映射待我审批或我已审批的一页，行保持后端顺序。 */
export function mapApprovalRoundPage(raw: ApprovalRoundPageRaw): ApprovalRoundPage {
  return {
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
    items: raw.items.map((item) => ({
      roundId: item.round_id,
      typeLabel: item.type_label,
      title: item.title,
      amount: item.amount,
      applicantName: item.applicant_name,
      submittedAt: item.submitted_at,
      status: item.status,
      currentStepNo: item.current_step_no,
      stepCount: item.step_count,
      myResult: item.my_result ?? null,
      myHandledAt: item.my_handled_at ?? null,
    })),
  };
}

/** 映射一张单子的详情，级和审批人保持后端顺序。 */
export function mapApprovalRoundDetail(raw: ApprovalRoundDetailRaw): ApprovalRoundDetail {
  return {
    roundId: raw.round_id,
    typeCode: raw.type_code,
    typeLabel: raw.type_label,
    businessId: raw.business_id,
    roundNo: raw.round_no,
    title: raw.title,
    summaryLines: raw.summary_lines,
    amount: raw.amount,
    applicantName: raw.applicant_name,
    flowLabel: raw.flow_label,
    status: raw.status,
    currentStepNo: raw.current_step_no,
    submittedAt: raw.submitted_at,
    endedAt: raw.ended_at ?? null,
    steps: raw.steps.map(mapRoundStep),
    canHandle: raw.can_handle,
    rounds: raw.rounds.map((round) => ({
      roundId: round.round_id,
      roundNo: round.round_no,
      flowLabel: round.flow_label,
      status: round.status,
      currentStepNo: round.current_step_no,
      submittedAt: round.submitted_at,
      endedAt: round.ended_at ?? null,
      steps: round.steps.map(mapRoundStep),
    })),
  };
}

/** 映射一轮里的一级，审批人保持后端顺序。 */
function mapRoundStep(step: ApprovalRoundDetailRaw["steps"][number]): ApprovalRoundStep {
  return {
    stepNo: step.step_no,
    approverType: step.approver_type,
    approvers: step.approvers.map((user) => ({ userId: user.user_id, name: user.name })),
    result: step.result ?? null,
    handledByName: step.handled_by_name ?? null,
    handledAt: step.handled_at ?? null,
    remark: step.remark ?? null,
  };
}
